/* eslint-disable vue/max-len */
import Vue from 'vue';
import {
  ListFactory, SuperDate, isValidObject, isArray, isValidArray,
} from '@razlet/utils';
import PricingDetalization from '../pricing/detalization';
import Fop from '../fop';
import {
  calculateDimensionVolume,
  DIMENSION_VOLUME_PRECISION,
  resolveVolumePrecision,
  roundVolume,
} from '~/utils/volume';

function diffObjects (oldObj, newObj) {
  const diff = {};

  const keys = new Set([
    ...Object.keys(oldObj || {}),
    ...Object.keys(newObj || {}),
  ]);

  for (const key of keys) {
    if (!oldObj || !newObj || oldObj[key] !== newObj[key]) {
      diff[key] = newObj[key];
    }
  }

  return diff;
}

export default class ShipmentHistory {
  date = null;

  id = null;

  userName = '';

  changes = [];

  dateFormatKeys = [
    'issueDay',
  ];

  booleanFormatKeys = [
    'issued',
    'delivered',
    'accepted',
    'refunded',
    'post',
  ];

  tableKeys = [
    'fopSold',
    'fopRefund',
    'priceSold',
    'priceRefund',
    'priceTotal',
    'dimensions',
    'attachments',
    'refundRules',
  ];

  isPost = false;
  isCorrespondence = false;

  constructor (data = {}) {
    if (!isValidObject(data)) return;

    this.isPost = data.isPost;
    this.isCorrespondence = data.isCorrespondence;
    this.volumePrecision = resolveVolumePrecision({ company: data.company });

    this.date = new SuperDate(new Date(data.timestamp));
    this.id = data.id;
    this.userName = data.username;
    this.formatChanges(data.changes);
  }

  formatChanges (changes) {
    if (!changes) return;

    const allChanges = [];

    const changesKeys = Object.keys(changes);

    changesKeys
      .filter(key => {
        const { prev, current } = changes[key];
        return !(prev == null && current == null) || this.tableKeys.includes(key);
      })
      .forEach(key => {
        const values = [];
        const value = changes[key];
        if (key === 'price') {
          const { prev, current } = value;

          const prevSold = prev?.sold || null;
          const currentSold = current?.sold || null;
          values.push({
            ...value,
            key: `${key}Sold`,
            prev: prevSold,
            current: currentSold,
          });

          const prevRefund = prev?.refund || null;
          const currentRefund = current?.refund || null;
          values.push({
            ...value,
            key: `${key}Refund`,
            prev: prevRefund,
            current: currentRefund,
          });

          const prevTotal = prev?.total || null;
          const currentTotal = current?.total || null;
          values.push({
            ...value,
            key: `${key}Total`,
            prev: prevTotal,
            current: currentTotal,
          });
        } else if (key === 'fop') {
          const { prev, current } = value;

          const prevSold = prev ? prev.filter(fop => fop.event === 'sold') : null;
          const currentSold = current ? current.filter(fop => fop.event === 'sold') : null;
          values.push({
            ...value,
            key: `${key}Sold`,
            prev: prevSold,
            current: currentSold,
          });

          const prevRefund = prev ? prev.filter(fop => fop.event === 'refund') : null;
          const currentRefund = current ? current.filter(fop => fop.event === 'refund') : null;
          values.push({
            ...value,
            key: `${key}Refund`,
            prev: prevRefund,
            current: currentRefund,
          });
        } else {
          values.push({
            ...value,
            key,
          });
        }
        allChanges.push(...values);
      });

    this.changes = allChanges
      .filter(value => {
        const { prev, current } = value;
        return !(prev == null && current == null) && JSON.stringify(prev) !== JSON.stringify(current);
      })
      .map(value => {
        const prev = this.#getChangeValue(value.key, value.prev, value.current);
        const current = this.#getChangeValue(value.key, value.current, value.prev);
        return {
          ...value,
          title: this.#getChangeTitle(value.key, value.prev),
          prev: this.#getChangeDiff(value.key, prev, current),
          current: this.#getChangeDiff(value.key, current, prev),
          isDate: this.dateFormatKeys.includes(value.key),
          isBoolean: this.booleanFormatKeys.includes(value.key),
          isTable: this.tableKeys.includes(value.key),
        };
      })
      .filter(i => {
        return !(i.prev == null && i.current == null);
      });
  }

  #getChangeValue (key, value, opposite) {
    // eslint-disable-next-line vue/max-len
    if ((value === '' || value === null || value === undefined || (isArray(value) && !value.length)) && !this.tableKeys.includes(key)) return null;
    if (key === 'volume') return roundVolume(value, this.volumePrecision);
    if (key === 'cargo' && value?.volume != null) {
      return {
        ...value,
        volume: roundVolume(value.volume, this.volumePrecision),
      };
    }
    if (this.dateFormatKeys.includes(key)) return new SuperDate(new Date(value));
    if (key === 'priceSold') {
      return opposite || value ? new PricingDetalization(value || {}, false, !!value) : null;
    }
    if (key === 'priceRefund') {
      return opposite || value ? new PricingDetalization(value || {}, true, !!value) : null;
    }
    if (key === 'priceTotal') {
      return opposite || value ? new PricingDetalization(value ? { total: value } : {}, false, !!value, true) : null;
    }
    if (key === 'fopSold') {
      return (opposite && opposite.length) || (value && value.length) ? ListFactory.create(Fop, value || []) : null;
    }
    if (key === 'fopRefund') {
      return (opposite && opposite.length) || (value && value.length) ? ListFactory.create(Fop, value || []) : null;
    }
    if (key === 'dimensions') {
      if (!isValidArray(opposite) && !isValidArray(value)) return null;
      return value
        ? value.map(item => {
          return {
            ...item,
            volume: roundVolume(calculateDimensionVolume(item), DIMENSION_VOLUME_PRECISION)
              .toFixed(DIMENSION_VOLUME_PRECISION),
          };
        })
        : [];
    }
    if (key === 'refundRules') {
      if (!isValidArray(opposite) && !isValidArray(value)) return null;
      return value
        ? value.map(item => {
          return {
            departure: item.departure,
            transfer: item.transfer,
            arrival: item.arrival,
            penalty: `${item.penalty}${item.measure}`,
          };
        })
        : [];
    }
    if (key === 'attachments') {
      if (!isValidArray(opposite) && !isValidArray(value)) return null;
      return value || [];
    }
    if (key === 'refundBid') {
      return value
        ? {
          status: Vue.prototype.$localize.t(`shipments.refund.statuses.${value.status}`),
          reason: Vue.prototype.$localize.t(`refundReasons.${value.reason}`),
          comment: value.comment || null,
          attachments: isValidArray(value.attachments) ? value.attachments.join(',') : null,
        }
        : null;
    }
    if (key === 'expeditor') {
      return value
        ? {
          accountNumber: value.accountNumber,
          contractor: value.contractor.name,
          inn: value.contractor.inn,
        }
        : null;
    }
    if (key === 'shipper' || key === 'consignee') {
      return value
        ? {
          ...value,
          contacts: isValidArray(value.contacts) ? value.contacts.map(v => v.value).join(',') : null,
        }
        : null;
    }
    return value;
  }

  #getChangeDiff (key, value, opposite) {
    if (value === null || !isValidObject(value) || this.tableKeys.includes(key) || this.dateFormatKeys.includes(key)) return value;
    return diffObjects(opposite, value);
  }

  #getChangeTitle (key) {
    let finalKey = key;
    if (key === 'osi') {
      if (this.isPost) finalKey = 'postAwb';
      if (this.isCorrespondence) finalKey = 'correspondenceAwb';
    }
    const localizeKey = `shipments.info.history.titles.${finalKey}`;
    const title = Vue.prototype.$localize.t(localizeKey);
    return title === localizeKey
      ? key
      : title;
  }

  get changesNames () {
    return this.changes.filter(change => change.prev || change.current).map(change => change.title);
  }
};
